import { Link } from "react-router-dom";
import { Home, Building2, Droplets, HardHat, TreePine, Fence, Warehouse, Wrench, ArrowRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

// Import service images
import houseWashingImg from "@/assets/services/house-washing.webp";
import gutterCleaningImg from "@/assets/services/gutter-cleaning.webp";
import vinylFenceImg from "@/assets/services/vinyl-fence-cleaning.webp";
import woodRestorationImg from "@/assets/services/wood-restoration.webp";
import roofWashingImg from "@/assets/services/roof-washing.webp";
import concreteCleaningImg from "@/assets/services/concrete-cleaning.webp";
import commercialBuildingImg from "@/assets/services/commercial-building.webp";
import dumpsterPadImg from "@/assets/services/dumpster-pad.webp";
import oxidationRemovalImg from "@/assets/services/oxidation-removal.webp";

const services = [
  { icon: Home, title: "House Washing", href: "/services/house-washing", image: houseWashingImg },
  { icon: Building2, title: "Roof Washing", href: "/services/roof-washing", image: roofWashingImg },
  { icon: HardHat, title: "Concrete Cleaning & Sealing", href: "/services/concrete-cleaning", image: concreteCleaningImg },
  { icon: HardHat, title: "Driveway Cleaning", href: "/services/driveway-cleaning", image: concreteCleaningImg },
  { icon: TreePine, title: "Deck Cleaning & Restoration", href: "/services/deck-cleaning", image: woodRestorationImg },
  { icon: Droplets, title: "Gutter Cleaning", href: "/services/gutter-cleaning", image: gutterCleaningImg },
  { icon: Fence, title: "Vinyl Fence Cleaning", href: "/services/vinyl-fence-cleaning", image: vinylFenceImg },
  { icon: TreePine, title: "Wood Restoration", href: "/services/wood-restoration", image: woodRestorationImg },
  { icon: Wrench, title: "Rust & Stain Removal", href: "/services/rust-stain-removal", image: oxidationRemovalImg },
  { icon: Warehouse, title: "Commercial Services", href: "/services/commercial", image: commercialBuildingImg },
];

const ServicesSection = () => {
  return (
    <section className="section-padding bg-background">
      <div className="container-custom">
        <div className="text-center mb-12 animate-fade-in">
          <h2 className="text-3xl md:text-4xl font-bold font-heading text-secondary mb-4">
            OUR <span className="text-primary">SERVICES</span>
          </h2>
          <p className="text-lg text-muted-foreground">
            Professional pressure washing and soft washing solutions for every surface
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => (
            <Link key={index} to={service.href}>
              <Card className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 h-full">
                {/* Image */}
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    width={400}
                    height={224}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-secondary/90 via-secondary/40 to-transparent" />
                  <div className="absolute bottom-4 left-4 bg-primary p-3 rounded-full">
                    <service.icon className="w-6 h-6 text-primary-foreground" />
                  </div>
                </div>

                {/* Content */}
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold font-heading text-secondary group-hover:text-primary transition-colors">
                    {service.title}
                  </h3>
                  <div className="flex items-center text-secondary font-semibold mt-3 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-2 transition-transform" />
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        {/* Additional Services */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-bold font-heading text-secondary mb-6">
            Additional Specialized Services
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            {[
              { name: "Patio Cleaning", href: "/services/patio-cleaning" },
              { name: "Sidewalk Cleaning", href: "/services/sidewalk-cleaning" },
              { name: "Brick & Paver Cleaning", href: "/services/brick-paver-cleaning" },
              { name: "Oxidation Removal", href: "/services/oxidation-removal" },
              { name: "Wood Staining & Sealing", href: "/services/wood-restoration" },
              { name: "Concrete Sealing", href: "/services/concrete-cleaning" },
            ].map((service, index) => (
              <Link
                key={index}
                to={service.href}
                className="px-4 py-2 bg-muted rounded-full text-muted-foreground font-medium hover:bg-primary hover:text-primary-foreground transition-colors"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
